import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, take, withLatestFrom } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import {
    CentralRegulatorActionFailure,
    LoadCentralRegulators,
    LoadCentralRegulatorsFailure,
    LoadCentralRegulatorsSuccess,
    LoadPlatformLicenseActivationsOfCentralRegulatorThatHaveNotBeenInvoiced,
    LoadPlatformLicenseActivationsOfCentralRegulatorThatHaveNotBeenInvoicedFailure,
    LoadPlatformLicenseActivationsOfCentralRegulatorThatHaveNotBeenInvoicedSuccess,
    ReloadRegulator,
    ReloadRegulatorFailure,
    ReloadRegulatorSuccess,
    RemoveCentralRegulatorAttachment,
    RemovePlatformLicenseActivationFromSelectedCentralRegulator,
    RemovePlatformLicenseActivationFromSelectedCentralRegulatorFailure,
    RemoveRegulatorNote,
    UpsertCentralRegulatorAddress,
    UpsertCentralRegulatorAddressFailure,
    UpsertCentralRegulatorPlatformLicenseActivation,
    UpsertCentralRegulatorPlatformLicenseActivationFailure,
    UpsertRegulator,
    UpsertRegulatorBankDetails,
    UpsertRegulatorBankDetailsFailure,
    UpsertRegulatorFailure,
    UpsertRegulatorNote,
    UpsertRegulatorSuccess
} from '../actions/central-regulators.actions';
import { Store } from '@ngrx/store';
import { CentralRegulatorControllerService, PersonDTO, PlatformLicenseActivationDTO } from '@iota/iota-management-portal-v4-client';
import { CentralRegulatorManagementControllerService } from '@iota/inh-core-v1-management-client';
import { LoadUserSuccess } from '../actions/user.actions';
import {
    LoadAllPersonsByCentralRegulator,
    LoadAllPersonsByCentralRegulatorFailure,
    LoadAllPersonsByCentralRegulatorSuccess
} from '../actions/person.actions';
import { selectedRegulatorId } from '../store';
import { DeleteCentralRegulatorAddress } from '../actions/central-regulators.actions';

@Injectable()
export class CentralRegulatorsEffects {
    loadCentralRegulators$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadUserSuccess, LoadCentralRegulators),
            mergeMap(() =>
                this.inhCoreApiService.getAllCentralRegulators().pipe(
                    take(1),
                    map((regulators) => LoadCentralRegulatorsSuccess({ regulators })),
                    catchError((error) => of(LoadCentralRegulatorsFailure({ error })))
                )
            )
        )
    );

    // Neither response carries the INH-Core key or deactivation fields the list needs, so refetch instead of patching.
    reloadCentralRegulatorsAfterRegulatorChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertRegulatorSuccess, ReloadRegulatorSuccess),
            map(() => LoadCentralRegulators())
        )
    );

    upsertRegulator$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertRegulator),
            mergeMap((action) =>
                this.apiService.upsertCentralRegulator(action.regulator).pipe(
                    map((regulator) => UpsertRegulatorSuccess({ regulator: regulator })),
                    catchError((error) => of(UpsertRegulatorFailure({ error })))
                )
            )
        )
    );

    reloadRegulator$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReloadRegulator),
            mergeMap(({ centralRegulatorId }) =>
                this.apiService.getCentralRegulator(centralRegulatorId).pipe(
                    take(1),
                    map((regulator) => ReloadRegulatorSuccess({ regulator: regulator })),
                    catchError((error) => of(ReloadRegulatorFailure({ error })))
                )
            )
        )
    );

    upsertCentralRegulatorAddress$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertCentralRegulatorAddress),
            mergeMap((action) =>
                this.apiService.upsertCentralRegulatorAddress(action.centralRegulatorId, action.address).pipe(
                    map(() => ReloadRegulator({ centralRegulatorId: action.centralRegulatorId })),
                    catchError((error) => of(UpsertCentralRegulatorAddressFailure({ error })))
                )
            )
        )
    );

    removeCentralRegulatorAddress$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeleteCentralRegulatorAddress),
            mergeMap(({ centralRegulatorId, addressId }) =>
                this.apiService.removeCentralRegulatorAddress(addressId).pipe(
                    map(() => ReloadRegulator({ centralRegulatorId })),
                    catchError((error) => of(CentralRegulatorActionFailure({ error })))
                )
            )
        )
    );

    upsertRegulatorBankDetails$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertRegulatorBankDetails),
            mergeMap((action) =>
                this.apiService.upsertCentralRegulatorBankDetails(action.centralRegulatorId, action.bankDetails).pipe(
                    map(() => ReloadRegulator({ centralRegulatorId: action.centralRegulatorId })),
                    catchError((error) => of(UpsertRegulatorBankDetailsFailure({ error })))
                )
            )
        )
    );

    loadAllPersonsByCentralRegulator$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadAllPersonsByCentralRegulator),
            mergeMap((action) =>
                this.apiService.getAllPersonsByCentralRegulator(action.centralRegulatorId).pipe(
                    map((persons: PersonDTO[]) =>
                        LoadAllPersonsByCentralRegulatorSuccess({ persons, centralRegulatorId: action.centralRegulatorId })
                    ),
                    catchError((error) => of(LoadAllPersonsByCentralRegulatorFailure({ error })))
                )
            )
        )
    );

    upsertRegulatorNote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertRegulatorNote),
            withLatestFrom(this.store.select(selectedRegulatorId)),
            mergeMap(([{ note }, centralRegulatorId]) => {
                return this.apiService.upsertCentralRegulatorNote(centralRegulatorId, note).pipe(
                    map(() => ReloadRegulator({ centralRegulatorId })),
                    catchError((error) => of(UpsertCentralRegulatorAddressFailure({ error })))
                );
            })
        )
    );

    removeRegulatorNote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RemoveRegulatorNote),
            withLatestFrom(this.store.select(selectedRegulatorId)),
            mergeMap(([{ noteId }, centralRegulatorId]) =>
                this.apiService.removeCentralRegulatorNote(noteId).pipe(
                    map(() => ReloadRegulator({ centralRegulatorId })),
                    catchError((error) => of(UpsertCentralRegulatorAddressFailure({ error })))
                )
            )
        )
    );

    upsertCentralRegulatorPlatformLicenseActivation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpsertCentralRegulatorPlatformLicenseActivation),
            withLatestFrom(this.store.select(selectedRegulatorId)),
            mergeMap(([{ plaformLicenseActivation }, centralRegulatorId]) => {
                const platformLicenseActivationWithCentralRegulatorId: PlatformLicenseActivationDTO = {
                    ...plaformLicenseActivation,
                    parentSkId: centralRegulatorId
                };

                return this.apiService
                    .upsertPlatformLicenseActivation(centralRegulatorId, platformLicenseActivationWithCentralRegulatorId)
                    .pipe(
                        map(() => ReloadRegulator({ centralRegulatorId })),
                        catchError((error) => of(UpsertCentralRegulatorPlatformLicenseActivationFailure({ error })))
                    );
            })
        )
    );

    removePlatformLicenseActivationFromSelectedCentralRegulator$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RemovePlatformLicenseActivationFromSelectedCentralRegulator),
            withLatestFrom(this.store.select(selectedRegulatorId)),
            mergeMap(([{ platformLicenseActivationId }, centralRegulatorId]) => {
                return this.apiService.removePlatformLicenseActivation(centralRegulatorId, platformLicenseActivationId).pipe(
                    map(() => ReloadRegulator({ centralRegulatorId })),
                    catchError((error) => of(RemovePlatformLicenseActivationFromSelectedCentralRegulatorFailure({ error })))
                );
            })
        )
    );

    loadPlatformLicenseActivationsOfCentralRegulatorThatHaveNotBeenInvoiced$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LoadPlatformLicenseActivationsOfCentralRegulatorThatHaveNotBeenInvoiced),
            mergeMap(({ centralRegulatorId }) =>
                this.apiService.getAllPlatformLicenseActivationsThatHaveNotBeenInvoiced(centralRegulatorId).pipe(
                    take(1),
                    map((licenseActivationIdsThatHaveNotBeenInvoiced) =>
                        LoadPlatformLicenseActivationsOfCentralRegulatorThatHaveNotBeenInvoicedSuccess({
                            licenseActivationIdsThatHaveNotBeenInvoiced
                        })
                    ),
                    catchError((error) => of(LoadPlatformLicenseActivationsOfCentralRegulatorThatHaveNotBeenInvoicedFailure({ error })))
                )
            )
        )
    );

    removeCentralRegulatorAttachment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RemoveCentralRegulatorAttachment),
            mergeMap(({ centralRegulatorId, documentDescriptionId }) =>
                this.apiService.removeCentralRegulatorAttachment(documentDescriptionId).pipe(
                    take(1),
                    map(() => ReloadRegulator({ centralRegulatorId })),
                    catchError((error) => of(CentralRegulatorActionFailure({ error })))
                )
            )
        )
    );

    constructor(
        private store: Store,
        private actions$: Actions,
        private apiService: CentralRegulatorControllerService,
        private inhCoreApiService: CentralRegulatorManagementControllerService
    ) {}
}
